import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Gamepad2, BookOpen, Hash, Star, ArrowRight, Sparkles, Type, Calculator, BarChart3, GraduationCap, Leaf, Languages, Volume2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import ArabicLettersGame from "@/components/games/ArabicLettersGame";
import NumbersGame from "@/components/games/NumbersGame";
import MemoryGame from "@/components/games/MemoryGame";
import ColorShapesGame from "@/components/games/ColorShapesGame";
import WordOrderGame from "@/components/games/WordOrderGame";
import MathGame from "@/components/games/MathGame";
import DrMathGame from "@/components/games/DrMathGame";
import ComputerGame from "@/components/games/ComputerGame";
import ScienceNatureGame from "@/components/games/ScienceNatureGame";
import EnglishGame from "@/components/games/EnglishGame";
import IslamicGame from "@/components/games/IslamicGame";
import PhonicsGame from "@/components/games/PhonicsGame";
import MentalMathGame from "@/components/games/MentalMathGame";
import { Monitor } from "lucide-react";
import type { Difficulty } from "@/components/games/difficulty";
import { getDifficultyConfig } from "@/components/games/difficulty";

const games = [
  { id: "drmath", title: "منهج دكتور ماس", description: "٨ مستويات متدرجة لتعلّم الرياضيات!", icon: GraduationCap, color: "from-amber-500 to-yellow-600", emoji: "🧮", hasDifficulty: false },
  { id: "letters", title: "تعلّم الحروف العربية", description: "تعلّم الحروف مع رسوم متحركة ممتعة!", icon: BookOpen, color: "from-emerald-400 to-teal-500", emoji: "🔤", hasDifficulty: true },
  { id: "numbers", title: "تعلّم الأرقام", description: "عدّ واختر الرقم الصحيح!", icon: Hash, color: "from-blue-400 to-indigo-500", emoji: "🔢", hasDifficulty: true },
  { id: "math", title: "العمليات الحسابية", description: "جمع وطرح بطريقة ممتعة!", icon: Calculator, color: "from-yellow-400 to-orange-500", emoji: "🧮", hasDifficulty: true },
  { id: "words", title: "ترتيب الكلمات", description: "رتّب الكلمات لتكوّن جملة صحيحة!", icon: Type, color: "from-cyan-400 to-blue-500", emoji: "📝", hasDifficulty: true },
  { id: "memory", title: "لعبة الذاكرة", description: "اكتشف الأزواج المتشابهة!", icon: Gamepad2, color: "from-purple-400 to-pink-500", emoji: "🧠", hasDifficulty: true },
  { id: "shapes", title: "الأشكال والألوان", description: "تعرّف على الأشكال والألوان!", icon: Star, color: "from-orange-400 to-red-500", emoji: "🎨", hasDifficulty: true },
  { id: "computer", title: "تعلّم الكمبيوتر", description: "تعرّف على أجزاء الحاسوب واستخداماته!", icon: Monitor, color: "from-cyan-500 to-blue-700", emoji: "💻", hasDifficulty: true },
  { id: "science", title: "العلوم والطبيعة", description: "اكتشف عجائب الطبيعة والعلوم!", icon: Leaf, color: "from-green-500 to-emerald-700", emoji: "🌍", hasDifficulty: true },
  { id: "english", title: "تعلّم الإنجليزية", description: "تعلّم كلمات إنجليزية بطريقة ممتعة!", icon: Languages, color: "from-violet-500 to-fuchsia-700", emoji: "🇬🇧", hasDifficulty: true },
  { id: "islamic", title: "التربية الإسلامية", description: "تعلّم أركان الإسلام والأذكار والآداب!", icon: BookOpen, color: "from-teal-500 to-emerald-700", emoji: "🕌", hasDifficulty: true },
  { id: "phonics", title: "English Phonics", description: "تعلّم أصوات الحروف الإنجليزية وتكوين الكلمات!", icon: Volume2, color: "from-blue-500 to-indigo-700", emoji: "🔤", hasDifficulty: true },
  { id: "mentalmath", title: "الحساب الذهني", description: "تدريب على الجمع والطرح والضرب والقسمة بسرعة!", icon: Calculator, color: "from-rose-500 to-red-700", emoji: "🧮", hasDifficulty: false },
];

const difficultyOptions: { value: Difficulty; label: string; emoji: string; desc: string; color: string }[] = [
  { value: "easy", label: "سهل", emoji: "🟢", desc: "٧ أسئلة - وقت أكثر", color: "from-green-400 to-emerald-500" },
  { value: "medium", label: "متوسط", emoji: "🟡", desc: "١٠ أسئلة - وقت عادي", color: "from-yellow-400 to-amber-500" },
  { value: "hard", label: "صعب", emoji: "🔴", desc: "١٥ سؤال - وقت أقل", color: "from-red-400 to-rose-500" },
];

const InteractiveLearning = () => {
  const [activeGame, setActiveGame] = useState<string | null>(null);
  const [pendingGame, setPendingGame] = useState<string | null>(null);
  const [difficulty, setDifficulty] = useState<Difficulty>("medium");
  const [selectedChildId, setSelectedChildId] = useState<string>("");
  const { profile } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const nurseryId = profile?.nursery_id;
  const [startTime] = useState(Date.now());

  const { data: children } = useQuery({
    queryKey: ["children-for-games", nurseryId],
    queryFn: async () => {
      const { data } = await supabase.from("children").select("id, full_name").eq("nursery_id", nurseryId!).order("full_name");
      return data || [];
    },
    enabled: !!nurseryId,
  });

  const handleGameClick = (gameId: string) => {
    const game = games.find((g) => g.id === gameId);
    if (game && !game.hasDifficulty) {
      setActiveGame(gameId);
    } else {
      setPendingGame(gameId);
    }
  };

  const startGameWithDifficulty = (d: Difficulty) => {
    setDifficulty(d);
    setActiveGame(pendingGame);
    setPendingGame(null);
  };

  const handleBack = () => {
    setActiveGame(null);
    setPendingGame(null);
  };

  const handleGameComplete = async (gameType: string, result: { score: number; maxScore: number; stars: number }) => {
    if (!selectedChildId || !nurseryId) return;
    const duration = Math.floor((Date.now() - startTime) / 1000);
    const percentage = result.maxScore > 0 ? Math.round((result.score / result.maxScore) * 100) : 0;

    const { error } = await supabase.from("game_results").insert({
      child_id: selectedChildId,
      nursery_id: nurseryId,
      game_type: gameType,
      score: result.score,
      max_score: result.maxScore,
      percentage,
      stars: result.stars,
      duration_seconds: duration,
    });

    if (error) {
      console.error("Save game result error:", error);
    } else {
      toast({ title: "✅ تم حفظ النتيجة", description: `${result.score} نقطة — ${result.stars} نجوم` });
    }
  };

  const renderGame = (gameId: string) => {
    const onComplete = (r: { score: number; maxScore: number; stars: number }) => handleGameComplete(gameId, r);
    const d = difficulty;
    switch (gameId) {
      case "drmath": return <DrMathGame onComplete={onComplete} />;
      case "letters": return <ArabicLettersGame onComplete={onComplete} difficulty={d} />;
      case "numbers": return <NumbersGame onComplete={onComplete} difficulty={d} />;
      case "math": return <MathGame onComplete={onComplete} difficulty={d} />;
      case "words": return <WordOrderGame onComplete={onComplete} difficulty={d} />;
      case "memory": return <MemoryGame onComplete={onComplete} difficulty={d} />;
      case "shapes": return <ColorShapesGame onComplete={onComplete} difficulty={d} />;
      case "computer": return <ComputerGame onComplete={onComplete} difficulty={d} />;
      case "science": return <ScienceNatureGame onComplete={onComplete} difficulty={d} />;
      case "english": return <EnglishGame onComplete={onComplete} difficulty={d} />;
      case "islamic": return <IslamicGame onComplete={onComplete} difficulty={d} />;
      case "phonics": return <PhonicsGame onComplete={onComplete} difficulty={d} />;
      case "mentalmath": return <MentalMathGame onComplete={onComplete} />;
      default: return null;
    }
  };

  const pendingGameInfo = pendingGame ? games.find((g) => g.id === pendingGame) : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-primary" />
            التعلّم التفاعلي
          </h1>
          <p className="text-muted-foreground text-sm mt-1">ألعاب تعليمية تفاعلية بالرسوم المتحركة للأطفال</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate("/dashboard/game-reports")}>
            <BarChart3 className="w-4 h-4 ml-1" />
            تقارير الأداء
          </Button>
          {(activeGame || pendingGame) && (
            <Button variant="outline" onClick={handleBack}>
              <ArrowRight className="w-4 h-4 ml-2" />
              العودة للألعاب
            </Button>
          )}
        </div>
      </div>

      {/* Child Selection */}
      <Card className="shadow-card">
        <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <Label className="text-sm font-medium whitespace-nowrap">👦 اختر الطفل:</Label>
          <Select value={selectedChildId} onValueChange={setSelectedChildId}>
            <SelectTrigger className="w-full sm:w-[250px]">
              <SelectValue placeholder="اختر طفلاً لتسجيل النتائج" />
            </SelectTrigger>
            <SelectContent>
              {(children || []).map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!selectedChildId && (
            <p className="text-xs text-muted-foreground">⚠️ اختر طفلاً لحفظ نتائج الألعاب تلقائياً</p>
          )}
        </CardContent>
      </Card>

      <AnimatePresence mode="wait">
        {/* Difficulty Selection */}
        {pendingGame && pendingGameInfo ? (
          <motion.div
            key="difficulty"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="max-w-lg mx-auto space-y-6"
          >
            <Card className="shadow-elevated overflow-hidden">
              <CardContent className="p-0">
                <div className={`bg-gradient-to-br ${pendingGameInfo.color} p-6 text-center text-white`}>
                  <p className="text-5xl mb-3">{pendingGameInfo.emoji}</p>
                  <h2 className="text-xl font-bold">{pendingGameInfo.title}</h2>
                  <p className="text-sm opacity-90 mt-1">اختر مستوى الصعوبة</p>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-3 gap-3">
              {difficultyOptions.map((opt, i) => (
                <motion.div
                  key={opt.value}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Card
                    className="cursor-pointer overflow-hidden border-2 border-transparent hover:border-primary/30 transition-all shadow-card hover:shadow-elevated"
                    onClick={() => startGameWithDifficulty(opt.value)}
                  >
                    <CardContent className="p-0">
                      <div className={`bg-gradient-to-br ${opt.color} p-4 text-center text-white`}>
                        <p className="text-4xl mb-2">{opt.emoji}</p>
                        <h3 className="text-lg font-bold">{opt.label}</h3>
                        <p className="text-xs opacity-90 mt-1">{opt.desc}</p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.div>
        ) : !activeGame ? (
          <motion.div
            key="menu"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            {games.map((game, i) => (
              <motion.div
                key={game.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.1, type: "spring", stiffness: 200 }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <Card
                  className="cursor-pointer overflow-hidden border-2 border-transparent hover:border-primary/30 transition-all shadow-card hover:shadow-elevated"
                  onClick={() => handleGameClick(game.id)}
                >
                  <CardContent className="p-0">
                    <div className={`bg-gradient-to-br ${game.color} p-6 text-white`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-4xl mb-2">{game.emoji}</p>
                          <h3 className="text-lg font-bold">{game.title}</h3>
                          <p className="text-sm opacity-90 mt-1">{game.description}</p>
                        </div>
                        <motion.div
                          animate={{ rotate: [0, 10, -10, 0] }}
                          transition={{ repeat: Infinity, duration: 3, delay: i * 0.5 }}
                        >
                          <game.icon className="w-12 h-12 opacity-30" />
                        </motion.div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div
            key={activeGame}
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 30 }}
            transition={{ type: "spring", stiffness: 200 }}
          >
            {activeGame !== "drmath" && (
              <div className="flex justify-center mb-4">
                <span className="text-sm text-muted-foreground bg-muted px-3 py-1 rounded-full">
                  {getDifficultyConfig(difficulty).emoji} مستوى: {getDifficultyConfig(difficulty).label}
                </span>
              </div>
            )}
            {renderGame(activeGame)}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InteractiveLearning;
